/**
 * 見積の固定添付（カテゴリーごとに、承認後のお客様宛メールへ初期値として付ける PDF）
 * マスタ（RP の tbl_estimate_fixed_attachment。PDF は S3）と、見積が持つ控えの出し入れ
 *
 * 見積が持つもの
 *   fixedAttachments     : 控え [{ id, categoryCode, categoryName, fileName, fileKey, excluded }]。送信はこのキーで読むので、マスタを直しても過去の見積に付くものは変わらない
 *                          excluded: true は「添付しない」にしたもの（消さずに残して画面で分かるようにする。送らない・並びにも入れない）
 *   fixedAttachmentKinds : 初期値をもう入れた種別（KINDS の name）。開き直しても足し直さない（外したものが戻らない）
 *   mailAttachmentOrder  : 枠番号 1〜3 と固定添付の印（'f' + id）を、メールに添付する順に並べたもの（estimateManager）
 */
import * as backend from '@libs/backend'
import * as utils from '@libs/utils'

/**
 * 種別（マスタ画面の枠の並び。カテゴリーが増えたらここに足すだけ。API はコードと名前をそのまま保存する）
 *   code   : 見積明細の categoryCode
 *   name   : 種別の名前（マスタの category_name。見積の控えと突き合わせるキー）
 *   byName : true なら「明細の categoryName に name を含む」ことも見る（ラウンドプラスは 1 つのコードに 3 つの種別があるため）。
 *            無ければ code だけで判定し、name は枠の見出し（例：{ code: '5', name: '定期清掃' }）
 */
export const KINDS = [
  { code: '2', name: 'ラウンドクリーンプラス', byName: true },
  { code: '2', name: 'ラウンドウォッシュプラス', byName: true },
  { code: '2', name: 'ラウンドダブルプラス', byName: true }
]

/**
 * 並びの印（mailAttachmentOrder に入れる値）
 * @param {*} id マスタの行の id
 */
export function orderKey(id) {
  return 'f' + id
}

/**
 * マスタの一覧
 * API: estimateFixedAttachment/getList
 * @returns [{ id, categoryName, fileName, fileKey, fileSize, sortNo, updateDate, updateUserName }]
 */
export async function getList() {
  const res = await backend.searchData('estimateFixedAttachment/getList', {})
  return res.data && Array.isArray(res.data.data) ? res.data.data : []
}

/**
 * PDF を 1 本追加（種別の並びの最後）
 * API: estimateFixedAttachment/save
 * @param {*} kind 種別（KINDS の要素。code と name を送る）
 * @param {*} file File（PDF）
 * @param {*} userId 登録する人
 * @returns 追加した行
 */
export async function save(kind, file, userId) {
  const data = await readFileAsDataUrl(file)
  const res = await backend.postData('estimateFixedAttachment/save', { categoryCode: kind.code, categoryName: kind.name, name: file.name, data, updateUser: userId })
  return res.data && res.data.data ? res.data.data : null
}

/**
 * 1 行削除（S3 のファイルは残る）
 * API: estimateFixedAttachment/delete
 */
export async function remove(id) {
  await backend.postData('estimateFixedAttachment/delete', { id })
}

/**
 * 並びの保存（同じ種別の id を上から順に）
 * API: estimateFixedAttachment/saveOrder
 */
export async function saveOrder(ids, userId) {
  await backend.postData('estimateFixedAttachment/saveOrder', { ids, updateUser: userId })
}

/**
 * PDF を別タブで開く（マスタの行の id か、見積の控えのキー。タブは押した直後に開く（取り終わってから開くとポップアップとして止められる））
 * API: estimateFixedAttachment/file・estimateFixedAttachment/fileByKey
 * @param {*} target { id } または { fileKey }
 * @param {*} fileName タブの名前
 */
export async function openFile(target, fileName) {
  const tab = window.open('', '_blank')
  if (tab) {
    tab.document.title = fileName || 'PDF'
    tab.document.body.innerHTML = '<p style="font-family:sans-serif;padding:16px">PDF を読み込んでいます…</p>'
  }
  const file = target.id
    ? await backend.getBlob('estimateFixedAttachment/file', { id: target.id })
    : await backend.getBlob('estimateFixedAttachment/fileByKey', { key: target.fileKey })
  const url = (window.URL || window.webkitURL).createObjectURL(new Blob([file.data], { type: 'application/pdf' }))
  if (tab) {
    tab.location.href = url
  } else {
    window.open(url, '_blank')
  }
}

/**
 * 見積の明細に入っている固定添付の種別（KINDS の name。重複なし）
 * @param {*} estimate 見積りデータ
 * @returns ['ラウンドクリーンプラス', ...]
 */
export function kindsOf(estimate) {
  const res = []
  for (const d of (estimate && estimate.details) || []) {
    // 半角にするとカタカナも半角になるので、両方をそろえて比べる
    const name = utils.hankaku(d.categoryName || '') || ''
    for (const k of KINDS) {
      if (d.categoryCode != k.code) {
        continue
      }
      if (k.byName && name.indexOf(utils.hankaku(k.name)) == -1) {
        continue
      }
      if (!res.includes(k.name)) {
        res.push(k.name)
      }
    }
  }
  return res
}

/**
 * 見積の固定添付の控えを明細の種別とそろえる
 * ・明細に新しく入った種別：マスタのその種別の PDF を控えに足す（枠 3 つの後ろに並ぶ）。種別は「入れた」と記録し、開き直しても足し直さない
 * ・明細から消えた種別：その控えと記録を消す
 * ・控えを持つキーを変えない（送信はこのキーで読む）
 * @param {*} estimate 見積りデータ（fixedAttachments・fixedAttachmentKinds を直接書き換える）
 * @param {*} master マスタの一覧（getList）。null（読めなかった）なら何もしない（種別を「入れた」と記録してしまうと、次に開いても PDF が入らない）
 * @returns 足した控え [{ id, categoryCode, categoryName, fileName, fileKey }]
 */
export function sync(estimate, master) {
  if (!Array.isArray(master)) {
    return []
  }
  if (!Array.isArray(estimate.fixedAttachments)) {
    estimate.fixedAttachments = []
  }
  if (!Array.isArray(estimate.fixedAttachmentKinds)) {
    estimate.fixedAttachmentKinds = []
  }
  const kinds = kindsOf(estimate)
  const added = []

  // 消えた種別
  const gone = estimate.fixedAttachmentKinds.filter((k) => !kinds.includes(k))
  if (gone.length) {
    estimate.fixedAttachments = estimate.fixedAttachments.filter((f) => !gone.includes(f.categoryName))
    estimate.fixedAttachmentKinds = estimate.fixedAttachmentKinds.filter((k) => kinds.includes(k))
  }

  // 新しく入った種別
  for (const kind of kinds) {
    if (estimate.fixedAttachmentKinds.includes(kind)) {
      continue
    }
    for (const m of master.filter((r) => r.categoryName == kind)) {
      if (estimate.fixedAttachments.some((f) => f.id == m.id)) {
        continue
      }
      const f = { id: m.id, categoryCode: m.categoryCode, categoryName: m.categoryName, fileName: m.fileName, fileKey: m.fileKey, excluded: false }
      estimate.fixedAttachments.push(f)
      added.push(f)
    }
    estimate.fixedAttachmentKinds.push(kind)
  }
  return added
}

/**
 * 見積の固定添付を「添付しない」にする（控えは消さず excluded を立てる。画面で分かるように残す。戻すときは自分で添付してもらう）
 * @param {*} estimate 見積りデータ
 * @param {*} id マスタの行の id
 */
export function removeFromEstimate(estimate, id) {
  // 古い控えは excluded のキーを持たないことがあるので、要素ごと置き換えてリアクティブにする
  estimate.fixedAttachments = (estimate.fixedAttachments || []).map((f) => (f.id == id ? { ...f, excluded: true } : f))
}

/**
 * 「添付しない」にした固定添付を添付するに戻す（並びは枠と固定添付の最後）
 * @param {*} estimate 見積りデータ
 * @param {*} id マスタの行の id
 */
export function restoreToEstimate(estimate, id) {
  estimate.fixedAttachments = (estimate.fixedAttachments || []).map((f) => (f.id == id ? { ...f, excluded: false } : f))
}

/**
 * メールに付ける固定添付（「添付しない」にしたものを除く）
 * @param {*} estimate 見積りデータ
 */
export function activeOf(estimate) {
  return ((estimate && estimate.fixedAttachments) || []).filter((f) => !f.excluded)
}

/**
 * 複写・コピーのときに控えを捨てる（開いたときの sync でマスタの今の PDF が入る）
 * @param {*} estimate 複写・コピーした見積りデータ
 */
export function resetForCopy(estimate) {
  estimate.fixedAttachments = []
  estimate.fixedAttachmentKinds = []
  if (Array.isArray(estimate.mailAttachmentOrder)) {
    estimate.mailAttachmentOrder = estimate.mailAttachmentOrder.filter((n) => typeof n == 'number')
  }
}

/**
 * 控えを、メールの添付一覧で枠の添付と同じように扱える形にする
 * （originalName・fileId は枠の添付と同じ名前。fixed: true で見分ける）
 * @param {*} f 控え
 */
export function toAttachment(f) {
  return { ...f, fixed: true, key: orderKey(f.id), originalName: f.fileName, fileId: orderKey(f.id) }
}

/**
 * File → base64 の data URL（バックへ送って S3 に上げる）
 */
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}
